import { useApp } from './useApp';
import { usePermissions } from './usePermissions';
import { useUserCompaniesCount, useUserCompaniesCountState } from './useUserCompaniesCount';
import { canSeePortalAsesor, portalAsesorMode, shouldLandOnPortal, type PortalAsesorMode } from '@/lib/canSeePortalAsesor';

/**
 * FASE 5 Sub-PR 5.2 — gate (hook) del Portal del Asesor.
 *
 * Wrapper REACTIVO sobre la fuente única `canSeePortalAsesor` (`@/lib/...`):
 * recoge user / permisos / count de empresas y delega la decisión. El mismo
 * predicado lo usa el `showIf` del menú en `pages/Layout.jsx`, así que el link
 * del sidebar y este guard de la ruta `/portal-asesor` NO pueden divergir.
 * Ver `canSeePortalAsesor` para los modos cubiertos.
 *
 * Loading conservador: mientras `useApp` carga devuelve `false`.
 */
export function useCanSeePortalAsesor(): boolean {
  const { user, loading } = useApp();
  const { hasPermission } = usePermissions();
  const userCompaniesCount = useUserCompaniesCount();

  if (loading) return false;
  return canSeePortalAsesor({ user, hasPermission, userCompaniesCount });
}

/**
 * Modo de acceso al portal: `'firm'` (asesoría/despacho real), `'group'`
 * (grupo de sociedades propio) o `null` (sin acceso). El shell lo usa para
 * adaptar el branding ("Mis empresas") y ocultar las pestañas de despacho.
 * Conservador durante la carga: devuelve `null`.
 */
export function usePortalAsesorMode(): PortalAsesorMode {
  const { user, loading } = useApp();
  const { hasPermission } = usePermissions();
  const userCompaniesCount = useUserCompaniesCount();

  if (loading) return null;
  return portalAsesorMode({ user, hasPermission, userCompaniesCount });
}

export function usePortalBasePath(): string {
  const mode = usePortalAsesorMode();
  return mode === 'group' ? '/mis-empresas' : '/portal-asesor';
}

/**
 * ¿La página de entrada `/` debe redirigir al portal en vez del Home? Wrapper
 * reactivo sobre `shouldLandOnPortal`: un firmMember real siempre va al portal;
 * una cuenta normal solo si gestiona >1 empresa. Conservador durante la carga:
 * devuelve `false` (deja que el Home sea el fallback hasta resolver la sesión).
 */
export function useShouldLandOnPortal(): boolean {
  const { user, loading } = useApp();
  const { hasPermission } = usePermissions();
  const userCompaniesCount = useUserCompaniesCount();

  if (loading) return false;
  return shouldLandOnPortal({ user, hasPermission, userCompaniesCount });
}

/**
 * ¿Pueden ya decidir los gates de ruta, o seguirían decidiendo a ciegas?
 *
 * Los tres hooks de arriba son conservadores durante la carga: devuelven
 * `false`/`null`. Para pintar un link eso es correcto —el link aparece cuando
 * se sabe—, pero para ELEGIR DESTINO no lo es: el destino conservador se
 * renderiza y se corrige al resolver, y eso el usuario lo ve como un salto.
 *
 * El caso real: una cuenta owner con varias empresas entraba a `/`, veía el
 * home de ARIA y saltaba a "Mis empresas" un instante después. `firmMember`
 * viene en `/me` y no parpadea; el modo 'group' depende del count de empresas,
 * que es un SEGUNDO fetch (`/api/auth/companies`) que nadie esperaba.
 *
 * Mismo patrón que el gate `!switchableLoading` del trigger del switcher en
 * `TopBar` (#815), por el mismo motivo.
 */
export function usePortalGateReady(): boolean {
  const { loading } = useApp();
  const { isResolved } = useUserCompaniesCountState();
  return !loading && isResolved;
}

export default useCanSeePortalAsesor;
